/* Copyright (c) 2020-present CloudIO Inc */

import { DataRecord } from '@cloudio-saas/datasource-types';
import { Direction } from '@mui/material';
import { AnyColumn, ItemID } from 'componentregistry';
import Row from 'components/DataGrid/grid/Row';
// import { Offscreen } from 'experimental/ExperimentalReact';
import React, { memo } from 'react';
import {
  useCurrentDataStore,
  useRecordsOrder,
} from '../../DataStoreReference/DataStoreReference';
import {
  Cell,
  CellComponentProps,
  DataContext,
  isBodyRegion,
  Region,
} from './types';
import { useGridBounds } from '../Context';

interface Props {
  CellComponent: React.FunctionComponent<CellComponentProps>;
  appUid: string;
  columnLefts?: number[];
  columnWidths: number[];
  columns: AnyColumn[];
  custom: boolean;
  dataContext: DataContext;
  direction?: Direction;
  endColumn: number;
  endRow: number;
  fixedLeftColumns: number;
  h1FixedLeftColumns?: number;
  h2FixedLeftColumns?: number;
  h3FixedLeftColumns?: number;
  headerRows?: number;
  highlightCell?: boolean;
  highlightRow?: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement>, cell: Cell<AnyColumn>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onRowDoubleClick?: (_rowIndex: number) => void;
  pageId: ItemID;
  region: Region;
  resetScroll: () => void;
  roleUid: string;
  rowHeight: number;
  showColumnLines: boolean;
  showRowLines: boolean;
  startColumn: number;
  startRow: number;
  actualStartRow: number;
  actualEndRow: number;
  totalRows: number;
  updateSelectedCells?: (
    rowIndex: number,
    columnIndex: number,
    value: string | number | undefined | null,
  ) => void;
  h1GroupColumnLefts?: number[];
  h1GroupColumnWidths?: number[];
  h1GroupColumns?: AnyColumn[];
  h2GroupColumnLefts?: number[];
  h2GroupColumnWidths?: number[];
  h2GroupColumns?: AnyColumn[];
  h3GroupColumnLefts?: number[];
  h3GroupColumnWidths?: number[];
  h3GroupColumns?: AnyColumn[];
  h1FixedRightColumns?: number;
  h2FixedRightColumns?: number;
  h3FixedRightColumns?: number;
  fixedRightColumns?: number;
  bodyHeight?: number;
  bodyWidth?: number;
}

function Cells(props: Props): React.ReactElement | null {
  const {
    CellComponent,
    appUid,
    columnLefts,
    columnWidths,
    columns,
    custom,
    dataContext,
    direction,
    endColumn,
    endRow,
    fixedLeftColumns = 0,
    h1FixedLeftColumns = 0,
    h2FixedLeftColumns = 0,
    h3FixedLeftColumns = 0,
    headerRows,
    highlightCell,
    highlightRow,
    onClick,
    onKeyDown,
    onRowDoubleClick,
    pageId,
    region,
    resetScroll,
    roleUid,
    rowHeight,
    showColumnLines,
    showRowLines,
    startColumn,
    startRow,
    totalRows,
    updateSelectedCells,
    h1GroupColumnLefts = [],
    h1GroupColumnWidths = [],
    h1GroupColumns = [],
    h2GroupColumnLefts = [],
    h2GroupColumnWidths = [],
    h2GroupColumns = [],
    h3GroupColumnLefts = [],
    h3GroupColumnWidths = [],
    h3GroupColumns = [],
    h1FixedRightColumns = 0,
    h2FixedRightColumns = 0,
    h3FixedRightColumns = 0,
    fixedRightColumns = 0,
    bodyHeight = 0,
    bodyWidth = 0,
    // actualStartRow,
    // actualEndRow,
  } = props;

  let top = 0;
  let row = 0;

  const rows: React.ReactNode[] = [];
  const recordIds = useRecordsOrder();
  const store = useCurrentDataStore<DataRecord>();
  const boundSignal = useGridBounds();

  // console.log('Cells', { startRow, endRow, totalRows });
  // useWhyDidYouUpdate('Cells', { ...props });

  // store will be null for List grid
  if (
    store &&
    recordIds.length === 0 &&
    isBodyRegion(region) &&
    !dataContext.selectField
  )
    return null;

  const groupHeader = (rowIndex: number) =>
    headerRows
      ? dataContext?.groupHeadersRows &&
        dataContext?.groupHeadersRows - rowIndex > 0
      : false;

  const getFixedLeftColumns = (rowIndex: number) => {
    return groupHeader(rowIndex)
      ? rowIndex === 0
        ? h1FixedLeftColumns
        : rowIndex === 1
        ? h2FixedLeftColumns
        : rowIndex === 2
        ? h3FixedLeftColumns
        : fixedLeftColumns
      : fixedLeftColumns;
  };

  const getFixedRightColumns = (rowIndex: number) => {
    return groupHeader(rowIndex)
      ? rowIndex === 0
        ? h1FixedRightColumns
        : rowIndex === 1
        ? h2FixedRightColumns
        : rowIndex === 2
        ? h3FixedRightColumns
        : fixedRightColumns
      : fixedRightColumns;
  };

  const getColumns = (rowIndex: number) => {
    return groupHeader(rowIndex)
      ? rowIndex === 0
        ? h1GroupColumns
        : rowIndex === 1
        ? h2GroupColumns
        : rowIndex === 2
        ? h3GroupColumns
        : columns
      : columns;
  };

  const getColumnLefts = (rowIndex: number) => {
    return groupHeader(rowIndex)
      ? rowIndex === 0
        ? h1GroupColumnLefts
        : rowIndex === 1
        ? h2GroupColumnLefts
        : rowIndex === 2
        ? h3GroupColumnLefts
        : columnLefts
      : columnLefts;
  };

  const getColumnWidths = (rowIndex: number) => {
    return groupHeader(rowIndex)
      ? rowIndex === 0
        ? h1GroupColumnWidths
        : rowIndex === 1
        ? h2GroupColumnWidths
        : rowIndex === 2
        ? h3GroupColumnWidths
        : columnWidths
      : columnWidths;
  };

  function getStartEndColumns(rowIndex: number) {
    let _startColumn = 0;
    const footerRows = dataContext.footerRows ?? 0;
    const bound = boundSignal.peek();
    const { left } = bound.pos;
    const totalColumns = getColumns(rowIndex).length;
    let _endColumn = totalColumns - 1;
    let w = 0;
    const _columnWidths = getColumnWidths(rowIndex);
    for (
      let i = getFixedLeftColumns(rowIndex);
      i < totalColumns - getFixedRightColumns(rowIndex);
      i++
    ) {
      w += _columnWidths[i];
      if (w >= left) {
        _startColumn = i;
        break;
      }
    }
    const right = left + bodyWidth;
    for (
      let i = _startColumn;
      i < totalColumns - getFixedRightColumns(rowIndex);
      i++
    ) {
      if (
        w >= right ||
        i === totalColumns - getFixedRightColumns(rowIndex) - 1
      ) {
        _endColumn = i;
        break;
      }
      w += _columnWidths[i + 1];
    }
    const result = {
      endColumn: Math.min(
        _endColumn,
        totalColumns - getFixedRightColumns(rowIndex) - 1,
      ),
      endRow: Math.min(
        totalRows - footerRows - 1,
        (headerRows ?? 0) + Math.ceil((top + bodyHeight) / rowHeight) - 1,
      ),
      startColumn: Math.max(getFixedLeftColumns(rowIndex), _startColumn),
      startRow: Math.max(
        headerRows ?? 0,
        headerRows ?? 0 + Math.floor(top / rowHeight),
      ),
    };
    return { startColumn: result.startColumn, endColumn: result.endColumn };
  }

  for (
    let rowIndex = startRow;
    rowIndex <= endRow && rowIndex < totalRows;
    rowIndex++
  ) {
    if (
      dataContext.showFilters &&
      rowIndex === ((dataContext?.headerRows as number) ?? 0) - 1
    ) {
      top = 0;
    } else if (
      ['header-center', 'header-left', 'header-right'].includes(region)
    ) {
      top = rowIndex * rowHeight;
    } else if (
      ['footer-center', 'footer-left', 'footer-right'].includes(region)
    ) {
      top = row * rowHeight;
    } else if (isBodyRegion(region)) {
      top = (rowIndex - (headerRows ?? 0)) * rowHeight;
    }

    const recordId = recordIds[rowIndex - (headerRows ?? 0)];
    row++;

    const _fixedLeftColumns = getFixedLeftColumns(rowIndex);

    const _fixedRightGroupColumns = getFixedRightColumns(rowIndex);

    const _columns = getColumns(rowIndex);

    const _columnLefts = getColumnLefts(rowIndex);

    const _columnWidths = getColumnWidths(rowIndex);

    const { startColumn: __startColumn, endColumn: __endColumn } =
      getStartEndColumns(rowIndex);

    const _endColumn = groupHeader(rowIndex)
      ? region === 'header-left' && _fixedLeftColumns > 0
        ? _fixedLeftColumns - 1
        : region === 'header-right'
        ? _columns.length - 1
        : __endColumn
      : endColumn;

    const _startColumn = groupHeader(rowIndex)
      ? region === 'header-left'
        ? 0
        : region === 'header-right' && _fixedRightGroupColumns > 0
        ? _columns.length - _fixedRightGroupColumns
        : __startColumn
      : startColumn;

    const node = (
      // <Offscreen
      //   key={`${region}-${rowIndex}-${recordId}`}
      //   mode={
      //     rowIndex >= actualStartRow && rowIndex <= actualEndRow
      //       ? 'visible'
      //       : 'unstable-defer-without-hiding'
      //   }
      // >
      <Row
        CellComponent={CellComponent}
        appUid={appUid}
        columnLefts={_columnLefts}
        columnWidths={_columnWidths ?? []}
        columns={_columns ?? []}
        custom={custom}
        dataContext={dataContext}
        direction={direction}
        endColumn={_endColumn}
        fixedLeftColumns={_fixedLeftColumns}
        headerRows={headerRows ?? 0}
        highlightCell={highlightCell}
        highlightRow={highlightRow}
        key={`${region}-${rowIndex}-${recordId}`}
        onClick={onClick}
        onKeyDown={onKeyDown}
        onRowDoubleClick={onRowDoubleClick}
        pageId={pageId}
        region={region}
        resetScroll={resetScroll}
        roleUid={roleUid}
        rowHeight={rowHeight}
        rowIndex={rowIndex}
        showColumnLines={showColumnLines}
        showRowLines={showRowLines}
        startColumn={_startColumn}
        store={store}
        top={top}
        updateSelectedCells={updateSelectedCells}
      />
      // </Offscreen>
    );
    // cache.current[rowIndex] = node;

    rows.push(node);
  }
  // useWhyDidYouUpdate(region, { ...props });
  // console.log('after cells', rows.length);

  return <>{rows}</>;
}

export default memo<Props>(Cells);
